'use client'

import { createContext, useContext, useMemo, useState } from 'react'
import type { CartBooking } from '@cocoonspace/api'
import { useCartGetCartBooking } from '@cocoonspace/api'

import { useAuth } from '~/providers/auth-provider'

export type CartContextValue = {
	isSheetOpen: boolean
	setIsSheetOpen: (isOpen: boolean) => void
	booking?: CartBooking
	eventsCount: number
	isLoading: boolean
	isError: boolean
}

const CartContext = createContext<CartContextValue | null>(null)

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
	const { isAuth } = useAuth()
	const [isSheetOpen, setIsSheetOpen] = useState(false)

	const {
		data: cartBooking,
		isLoading,
		isError,
		refetch,
	} = useCartGetCartBooking({ query: { enabled: isAuth } })

	const value = useMemo<CartContextValue>(() => {
		const booking = cartBooking?.booking
		const eventsCount = cartBooking?.eventsCount ?? 0

		return {
			isSheetOpen,
			setIsSheetOpen,
			booking,
			eventsCount,
			isLoading,
			isError,
			refetch,
		}
	}, [cartBooking, isLoading, isError, refetch, isSheetOpen])

	return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = () => {
	const value = useContext(CartContext)

	if (!value) {
		throw new Error('useCart must be used within a CartProvider')
	}

	return value
}
